import React, { FC, useEffect, useState } from "react"
import { FlyoutProps } from "victory"
import { checkTimeForHours } from "../../utils/helpers"
import { colors } from "../../utils/theme"
import { ChartDataPoints } from "../../utils/types"
import ChartHoverCrosshair, {
  ChartHoverCrosshairBounds,
} from "./ChartHoverCrosshair"

const DataText: FC<{ data: ChartDataPoints; x: number; y: number }> = ({
  data,
  x,
  y,
}) => {
  const { isHour, hour, minute } = checkTimeForHours(`${data.y}`, data.unit)

  let unit = data.unit
  let dataY = data.other ? (data.other as number) : data.y
  if (unit === "min") {
    dataY = Math.round(dataY)
  } else {
    dataY = parseFloat(dataY.toFixed(2))
  }
  return (
    <g>
      <circle
        cx={(x ?? 0) + 16}
        cy={(y ?? 0) + 41}
        r={4}
        fill={data?.darkColor}
        strokeWidth={0}
      />
      {!isHour ? (
        <text
          x={(x ?? 0) + 24}
          y={(y ?? 0) + 46}
          strokeWidth={0}
          fill={colors.neutral.white}
          fontFamily="Lato"
          fontSize={14}
        >
          {data.name}: {dataY} {unit}
        </text>
      ) : (
        <text
          x={(x ?? 0) + 24}
          y={(y ?? 0) + 46}
          strokeWidth={0}
          fill={colors.neutral.white}
          fontFamily="Lato"
          fontSize={14}
        >
          {data.name}: {`${hour} hr ${minute} min`}
        </text>
      )}
    </g>
  )
}

const DataDate: FC<{ data: ChartDataPoints; x: number; y: number }> = ({
  data,
  x,
  y,
}) => (
  <text
    x={(x ?? 0) + 12}
    y={(y ?? 0) + 68}
    strokeWidth={0}
    fill={colors.neutral.white}
    fontStyle="italic"
    fontFamily="Lato"
    fontWeight={400}
    fontSize={14}
  >
    {data?.date}
  </text>
)

const ChartTooltip: FC<
  {
    datum?: ChartDataPoints
    width?: number
    otherData?: ChartDataPoints[][][]
    otherDataRange?: number
    /** When set, draws a vertical guide at the hovered date. */
    crosshair?: ChartHoverCrosshairBounds
  } & FlyoutProps
> = ({ x, y, datum, width, otherData, otherDataRange, crosshair }) => {
  const [nearbyData, setNearbyData] = useState<ChartDataPoints[] | undefined>(
    undefined
  )

  useEffect(() => {
    if (otherData && otherDataRange) {
      const newNearbyData: ChartDataPoints[] = []
      otherData?.forEach((i) =>
        i.forEach((j) =>
          j.forEach((k) => {
            if (
              // in the same column
              k.x === datum?.x &&
              // not the same point
              k.name !== datum?.name &&
              // within other data range
              k.y > datum?.y - otherDataRange &&
              k.y < datum?.y + otherDataRange
            ) {
              newNearbyData.push(k)
            }
          })
        )
      )
      setNearbyData(newNearbyData)
    }
  }, [datum?.label, datum?.name, datum?.x, datum?.y, otherData, otherDataRange])

  if (!datum || !x || !y) {
    return null
  }

  return (
    <g>
      {crosshair ? <ChartHoverCrosshair x={x} {...crosshair} /> : null}
      <rect
        x={x}
        y={(y ?? 0) + 20}
        height={62 + (nearbyData?.length ?? 0) * 24}
        width={width}
        rx={4}
        ry={4}
        fill={colors.neutral.space}
      />
      <DataText data={datum} x={x} y={y} />
      {nearbyData?.map((data, index) => (
        <DataText key={index} data={data} x={x} y={y + (index + 1) * 24} />
      ))}
      <DataDate data={datum} x={x} y={y + (nearbyData?.length ?? 0) * 24} />
    </g>
  )
}

export default ChartTooltip
